import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, CheckCircle2, ArrowLeft } from "lucide-react";

export default function Signup() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [allowedDomains, setAllowedDomains] = useState([]);

  useEffect(() => {
    api.get("/auth/signup-info").then((r) => setAllowedDomains(r.data?.allowed_domains || [])).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await api.post("/auth/signup", form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't submit your request. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafb] flex flex-col p-6 md:p-16">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-[#0033CC] flex items-center justify-center rounded-sm">
          <Mail className="w-4 h-4 text-white" strokeWidth={2} />
        </div>
        <span className="font-heading text-lg font-medium tracking-tight">Internal Inbox</span>
      </div>

      <div className="flex-1 flex items-center justify-center py-8">
        <div className="max-w-sm w-full" data-testid="signup-page">
          {!submitted ? (
            <>
              <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-3">Request access</p>
              <h1 className="font-heading text-3xl font-medium tracking-tight mb-2">Join your team's inbox</h1>
              <p className="text-sm text-zinc-600 mb-8 leading-relaxed">
                Fill out the form. An admin will approve your account and email you a sign-in link.
              </p>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
                <div>
                  <Label htmlFor="name" className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Full name</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 h-11 rounded-md border-zinc-300 focus-visible:ring-[#0033CC]"
                    required autoFocus
                    data-testid="signup-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Work email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1.5 h-11 rounded-md border-zinc-300 focus-visible:ring-[#0033CC]"
                    required
                    data-testid="signup-email-input"
                  />
                  {allowedDomains.length > 0 && (
                    <p className="text-[11px] text-zinc-500 mt-1">
                      Must end in <span className="font-mono">@{allowedDomains[0]}</span>
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Mobile (for SMS sign-in)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1.5 h-11 rounded-md border-zinc-300 focus-visible:ring-[#0033CC]"
                    placeholder="+15165551234"
                    data-testid="signup-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="reason" className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Why do you need access? <span className="font-normal normal-case tracking-normal text-zinc-400">(optional)</span></Label>
                  <textarea
                    id="reason"
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    rows={3}
                    className="mt-1.5 w-full text-sm px-3 py-2 rounded-md border border-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0033CC]"
                    placeholder="I'm joining the submissions team starting Monday…"
                    data-testid="signup-reason-input"
                  />
                </div>

                {error && (
                  <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-md" data-testid="signup-error">
                    {error}
                  </div>
                )}

                <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#0033CC] hover:bg-[#002299] text-white rounded-md font-medium" data-testid="signup-submit-button">
                  {submitting ? "Submitting…" : "Request access"}
                </Button>

                <Link to="/login" className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1 justify-center pt-1">
                  <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Already have an account? Sign in
                </Link>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="signup-success">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" strokeWidth={1.5} />
              <h1 className="font-heading text-2xl font-medium tracking-tight mb-2">Request submitted</h1>
              <p className="text-sm text-zinc-600 mb-6 leading-relaxed">
                Thanks, <span className="font-medium">{form.name}</span>. An admin will review your request
                and email <span className="font-mono">{form.email}</span> a sign-in link once approved.
              </p>
              <Link to="/login" className="text-sm text-[#0033CC] hover:underline inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-zinc-400">© 2026 Internal Inbox</div>
    </div>
  );
}
